import React, { useEffect, useState } from "react";
import Button from "../../components/staff/Button";
import type { AccountingOfficeDetail } from "../../utils/accountingOfficeDetail";
import {
  computeFirmAccountStatement,
  loadFirmStatementBillingContext,
} from "../../utils/accountingFirmStatement";
import { membershipDuesFiscalYearForOffice } from "../../utils/billingYear";
import { todayIso } from "../../utils/dates";
import {
  AccountingInsetPanel,
  AccountingPanel,
  AccountingPanelSubsection,
  AccountingSummaryRow,
  FEE_TYPE_LABEL_CLASS,
  SECTION_TITLE_CLASS,
} from "./accountingFirmPanels";

const EMPTY_VALUE = "—";

const ACCOUNT_STATEMENT_LABEL_TOOLTIPS = {
  totalPaymentReceived:
    "Total historical sum of all payments received and registered for this firm (All-time).",
  duesPaymentReceived:
    "All-time total of payments allocated exclusively to membership invoices (Dues, Funds, Assessments).",
  totalBalance:
    "Total accumulated debt of the firm, including all historical pending balances for both Memberships and Events.",
  duesBalance:
    "Outstanding balance exclusively for Annual Dues and Special Fund for the current fiscal year.",
  invoiceMeetingBalancePrepaids:
    "Outstanding balance for prepaid event fees included within the current annual membership invoice.",
} as const;

function formatStatementMoney(value: number): string {
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

type AccountingPaymentSummaryProps = {
  office: AccountingOfficeDetail;
  refreshKey?: number;
};

export default function AccountingPaymentSummary({
  office,
  refreshKey = 0,
}: AccountingPaymentSummaryProps): React.ReactElement {
  const [loading, setLoading] = useState(true);
  const [totalPaymentReceived, setTotalPaymentReceived] = useState<
    number | null
  >(null);
  const [duesInvoiced, setDuesInvoiced] = useState<number | null>(null);
  const [totalBalance, setTotalBalance] = useState<number | null>(null);
  const [duesBalanceCurrentFiscalYear, setDuesBalanceCurrentFiscalYear] =
    useState<number | null>(null);
  const [
    invoiceMeetingBalanceCurrentFiscalYear,
    setInvoiceMeetingBalanceCurrentFiscalYear,
  ] = useState<number | null>(null);

  const firmId = String(office.firm ?? "").trim();
  const viewedOfficeFiscalYear = membershipDuesFiscalYearForOffice(
    todayIso(),
    office.billingStartDate,
  );

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      setLoading(true);
      if (!firmId) {
        if (!cancelled) {
          setTotalPaymentReceived(null);
          setDuesInvoiced(null);
          setTotalBalance(null);
          setDuesBalanceCurrentFiscalYear(null);
          setInvoiceMeetingBalanceCurrentFiscalYear(null);
          setLoading(false);
        }
        return;
      }

      try {
        const { docs, currentFiscalYearByOfficeId } =
          await loadFirmStatementBillingContext(firmId);
        if (cancelled) {
          return;
        }
        const statement = computeFirmAccountStatement(
          docs,
          currentFiscalYearByOfficeId,
        );
        setTotalPaymentReceived(statement.totalPaymentReceived);
        setDuesInvoiced(statement.duesInvoiced);
        setTotalBalance(statement.totalBalance);
        setDuesBalanceCurrentFiscalYear(statement.duesBalanceCurrentFiscalYear);
        setInvoiceMeetingBalanceCurrentFiscalYear(
          statement.invoiceMeetingBalanceCurrentFiscalYear,
        );
      } catch {
        if (!cancelled) {
          setTotalPaymentReceived(null);
          setDuesInvoiced(null);
          setTotalBalance(null);
          setDuesBalanceCurrentFiscalYear(null);
          setInvoiceMeetingBalanceCurrentFiscalYear(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [firmId, refreshKey]);

  const formatValue = (amount: number | null): string => {
    if (loading) {
      return "…";
    }
    if (amount === null) {
      return EMPTY_VALUE;
    }
    return formatStatementMoney(amount);
  };

  return (
    <AccountingPanel title="Account Statement">
      <div className="space-y-1">
        <AccountingSummaryRow
          label="Total Payment Received"
          labelTooltip={ACCOUNT_STATEMENT_LABEL_TOOLTIPS.totalPaymentReceived}
          value={formatValue(totalPaymentReceived)}
        />
        <AccountingSummaryRow
          label="Dues Payment Received"
          labelTooltip={ACCOUNT_STATEMENT_LABEL_TOOLTIPS.duesPaymentReceived}
          value={formatValue(duesInvoiced)}
        />
      </div>

      <AccountingInsetPanel className="mt-3">
        <div className="space-y-1">
          <AccountingSummaryRow
            label="Total Balance"
            labelTooltip={ACCOUNT_STATEMENT_LABEL_TOOLTIPS.totalBalance}
            value={formatValue(totalBalance)}
          />
        </div>
      </AccountingInsetPanel>

      <AccountingInsetPanel className="mt-3">
        <h3
          className={`${SECTION_TITLE_CLASS.replace("mb-4", "mb-3")} text-base`}
        >
          Present Fiscal Year
          <span className="ml-1 text-sm font-normal text-gray-font">
            (FY {viewedOfficeFiscalYear})
          </span>
        </h3>
        <div className="space-y-1">
          <AccountingSummaryRow
            label="Dues Balance"
            labelTooltip={ACCOUNT_STATEMENT_LABEL_TOOLTIPS.duesBalance}
            value={formatValue(duesBalanceCurrentFiscalYear)}
          />
          <AccountingSummaryRow
            label="Invoice Mtg. Balance (PREPAIDS)"
            labelTooltip={
              ACCOUNT_STATEMENT_LABEL_TOOLTIPS.invoiceMeetingBalancePrepaids
            }
            value={formatValue(invoiceMeetingBalanceCurrentFiscalYear)}
          />
        </div>
      </AccountingInsetPanel>

      <AccountingPanelSubsection
        title="Activity & Comments Log"
        action={
          <Button
            text="Full History"
            color="primary"
            variant="outlined"
            size="fixed"
            onClick={() => {}}
          />
        }
      >
        <p className={FEE_TYPE_LABEL_CLASS}>{EMPTY_VALUE}</p>
      </AccountingPanelSubsection>
    </AccountingPanel>
  );
}
